import { useState, useEffect, useRef } from "react";
import { buildGoogleFontsHref, loadGoogleFont, tryLoadGoogleFont, CUSTOM_FONT_FAMILY } from '../../../utils/fontLoading';

const systemFonts = [
	"Arial", "Courier New", "Georgia", "Tahoma",
	"Times New Roman", "Trebuchet MS", "Verdana"
];

const googleFonts = [
	"Roboto", "Open Sans", "Lato", "Montserrat", "Poppins",
	"Inter", "Oswald", "Raleway", "Nunito", "Playfair Display",
	"Merriweather", "Work Sans", "Rubik", "DM Sans", "Lora",
	"Bebas Neue", "Dancing Script", "Pacifico", "Quicksand", "Josefin Sans"
];

const fontSlots = [
	{ key: 'normal-normal', label: 'Regular',     weight: 'normal', style: 'normal', required: true },
	{ key: 'bold-normal',   label: 'Bold',        weight: 'bold',   style: 'normal' },
	{ key: 'normal-italic', label: 'Italic',      weight: 'normal', style: 'italic' },
	{ key: 'bold-italic',   label: 'Bold Italic', weight: 'bold',   style: 'italic' },
];

const ACCEPTED_FONT_EXTENSIONS = ['woff2', 'woff', 'ttf', 'otf'];
const MAX_FONT_BYTES = 5 * 1024 * 1024; // 5 MB

export default function FontsTab({
	font, campaignId,
	updateFontFields, updateCustomFont, uploadFont, deleteFont
}) {
	const [fontSource, setFontSource] = useState(font?.source || 'system');
	const [customFont, setCustomFont] = useState('');
	const [customFontStatus, setCustomFontStatus] = useState('idle'); // idle | checking | ok | error

	const [uploadProgress, setUploadProgress] = useState({}); // { 'normal-normal': 0..100 }
	const [uploadError, setUploadError] = useState({});       // { 'normal-normal': 'message' }
	const [deleting, setDeleting] = useState({});             // { 'normal-normal': true }

	// Google "Other" state
	const [showOther, setShowOther] = useState(false);

	// Upload state
	const [customName, setCustomName] = useState(font?.custom?.name || '');
	const [licenseConfirmed, setLicenseConfirmed] = useState(!!(font?.custom?.files?.length));

	// Skip local setState after unmount
	const mounted = useRef(true);
	useEffect(() => () => { mounted.current = false; }, []);
	const runIfMounted = (fn) => { if(mounted.current) fn(); };

	// Registered FontFace objects, keyed by `${weight}-${style}`
	const faceRefs = useRef({});

	// On mount: if a custom-name Google font is already saved, open the "Other" field and pre-fill it
	useEffect(() => {
		if(font?.source === 'google' && font?.family && !googleFonts.includes(font.family)){
			setShowOther(true);
			setCustomFont(font.family);
			setCustomFontStatus('ok');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Lazy-load Google preview fonts
	useEffect(() => {
		if(fontSource !== 'google') return;
		if(document.getElementById('gf-previews')) return;
		const link = document.createElement('link');
		link.id = 'gf-previews';
		link.rel = 'stylesheet';
		link.href = buildGoogleFontsHref(googleFonts, '400');
		document.head.appendChild(link);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fontSource])

	// Register already-uploaded faces when the so slot previews render in the real typeface.
	useEffect(() => {
		if(fontSource !== 'custom') return;
		if(typeof window !== 'undefined' && window.FontFace){
			Array.from(document.fonts).forEach(face => {
				if(face.family === CUSTOM_FONT_FAMILY) document.fonts.delete(face);
			});
		}
		faceRefs.current = {};
		(font?.custom?.files || []).forEach(f =>
			loadCustomFontFace(f.url, f.fontWeight, f.fontStyle));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fontSource])

	const getFontItemCss = (f, src) => {
		let o = "dropdown__item";
		if(font?.family === f && (font?.source || 'system') === src)
			o += ' dropdown__item-active';
		return o;
	}

	// Register an uploaded face for in-editor preview
	const loadCustomFontFace = async (url, weight, style) => {
		if(!url || typeof window === 'undefined' || !window.FontFace) return;
		const key = `${weight}-${style}`;
		const prev = faceRefs.current[key];
		if(prev){
			try { document.fonts.delete(prev); } catch (e) { /* not registered */ }
			delete faceRefs.current[key];
		}
		try {
			const face = new FontFace(CUSTOM_FONT_FAMILY, `url(${url})`, { weight, style });
			document.fonts.add(face);
			faceRefs.current[key] = face;
			await face.load();
		} catch (e) {
			// Best-effort preview; a failure here usually means a CORS-blocked file.
		}
	}

	// Drop an in-editor face (by reference) so previews / live text fall back to the browser-synthesized style immediately.
	const unregisterFace = (weight, style) => {
		const key = `${weight}-${style}`;
		const face = faceRefs.current[key];
		if(face){
			try { document.fonts.delete(face); } catch (e) { /* already gone */ }
			delete faceRefs.current[key];
		}
	}

	const deriveNameFromFile = (filename) => {
		let base = (filename || '').replace(/\.[^.]+$/, '');
		base = base
			.replace(/[-_]+/g, ' ')
			.replace(/\b(thin|extralight|light|regular|medium|semibold|bold|extrabold|black|italic|oblique)\b/gi, '')
			.replace(/\s+/g, ' ')
			.trim();
		return base || 'Custom Font';
	}

	const getFontFile = (weight, style) =>
		(font?.custom?.files || []).find(x => x.fontWeight === weight && x.fontStyle === style);


	const selectSystemFont = (f) => {
		setShowOther(false);
		updateFontFields({ family: f, source: 'system' });
	}

	const selectCuratedFont = (f) => {
		setShowOther(false);
		setCustomFont('');
		setCustomFontStatus('idle');
		loadGoogleFont(f);
		updateFontFields({ family: f, source: 'google' });
	}

	const openOther = () => {
		setShowOther(true);
	}

	// Verify a typed family resolves on Google Fonts before committing
	const verifyCustomFont = async (value) => {
		const fam = (value || '').trim();
		if(!fam){ setCustomFontStatus('idle'); return; }
		setCustomFontStatus('checking');
		const ok = await tryLoadGoogleFont(fam);
		if(ok) updateFontFields({ family: fam, source: 'google' });
		runIfMounted(() => setCustomFontStatus(ok ? 'ok' : 'error'));
	}

	const handleFontFileSelect = (slot, fileList) => {
		const file = fileList && fileList[0];
		if(!file) return;

		// Validate before uploading: extension + size. Surfaces a clear message
		// instead of waiting for a storage-rule rejection.
		const ext = (file.name.split('.').pop() || '').toLowerCase();
		if(!ACCEPTED_FONT_EXTENSIONS.includes(ext)){
			setUploadError(prev => ({ ...prev, [slot.key]: 'Unsupported file type. Use .woff2, .woff, .ttf, or .otf.' }));
			return;
		}
		if(file.size > MAX_FONT_BYTES){
			setUploadError(prev => ({ ...prev, [slot.key]: `File is too large (max ${Math.round(MAX_FONT_BYTES / (1024 * 1024))} MB).` }));
			return;
		}

		setUploadError(prev => ({ ...prev, [slot.key]: null }));
		setUploadProgress(prev => ({ ...prev, [slot.key]: 0 }));

		uploadFont(
			campaignId,
			slot.weight,
			slot.style,
			file,
			(snapshot) => {
				const total = snapshot.totalBytes || 1;
				const pct = Math.round((snapshot.bytesTransferred / total) * 100);
				runIfMounted(() => setUploadProgress(prev => ({ ...prev, [slot.key]: pct })));
			},
			async (url) => {
				runIfMounted(() => setUploadProgress(prev => { const n = { ...prev }; delete n[slot.key]; return n; }));

				const typed = (customName || '').trim();

				// The recipe runs against the parent's live { name, files }, so
				// two concurrent uploads each build on the other's committed
				// result instead of a captured snapshot — no stale read here.
				updateCustomFont(prev => {
					const files = [
						...prev.files.filter(x => !(x.fontWeight === slot.weight && x.fontStyle === slot.style)),
						{ fontWeight: slot.weight, fontStyle: slot.style, url },
					];
					const name = typed || prev.name || deriveNameFromFile(file.name);
					return { name, files };
				});

				// Reflect a derived label into the field when none was typed.
				if(!typed) runIfMounted(() => setCustomName(deriveNameFromFile(file.name)));

				await loadCustomFontFace(url, slot.weight, slot.style);
			},
			(error) => {
				runIfMounted(() => {
					setUploadProgress(prev => { const n = { ...prev }; delete n[slot.key]; return n; });
					setUploadError(prev => ({ ...prev, [slot.key]: (error && error.message) || 'Upload failed' }));
				});
			}
		);
	}

	// Persist the display label
	const renameCustom = () => {
		const name = (customName || '').trim();
		const files = font?.custom?.files || [];
		if(files.length === 0) return;
		updateFontFields({ custom: { name, files } });
	}

	// Re-activate an already-uploaded font
	const applyCustomFont = () => {
		const files = font?.custom?.files || [];
		if(files.length === 0) return;
		setShowOther(false);
		updateFontFields({ family: CUSTOM_FONT_FAMILY, source: 'custom' });
		files.forEach(f => loadCustomFontFace(f.url, f.fontWeight, f.fontStyle));
	}

	const removeWholeCustomFont = () => {
		const files = font?.custom?.files || [];
		setUploadError(prev => ({ ...prev, __all__: null }));
		setDeleting(prev => ({ ...prev, __all__: true }));

		Promise.all(files.map(f => new Promise((resolve) => {
			deleteFont(campaignId, f.fontWeight, f.fontStyle,
				() => resolve(true),
				() => resolve(false));
		}))).then((results) => {
			if(results.some(ok => !ok)){
				runIfMounted(() => {
					setDeleting(prev => { const n = { ...prev }; delete n.__all__; return n; });
					setUploadError(prev => ({ ...prev, __all__: 'Couldn’t delete every file — nothing was changed. Try again.' }));
				});
				return;
			}
			files.forEach(f => unregisterFace(f.fontWeight, f.fontStyle));
			updateFontFields({ family: 'Arial', source: 'system', custom: null });
			runIfMounted(() => {
				setDeleting(prev => { const n = { ...prev }; delete n.__all__; return n; });
				setCustomName('');
				setLicenseConfirmed(false);
				setFontSource('system');
			});
		});
	}

	const removeFontFile = (slot) => {
		if(slot.required || deleting[slot.key]) return;
		const current = font?.custom?.files || [];
		const present = current.some(x => x.fontWeight === slot.weight && x.fontStyle === slot.style);
		if(!present) return;

		setUploadError(prev => ({ ...prev, [slot.key]: null }));
		setDeleting(prev => ({ ...prev, [slot.key]: true }));

		deleteFont(
			campaignId,
			slot.weight,
			slot.style,
			() => {
				unregisterFace(slot.weight, slot.style);
				// Recipe over the parent's live files — no captured snapshot.
				updateCustomFont(prev => ({
					name: prev.name,
					files: prev.files.filter(x => !(x.fontWeight === slot.weight && x.fontStyle === slot.style)),
				}));
				runIfMounted(() => setDeleting(prev => { const n = { ...prev }; delete n[slot.key]; return n; }));
			},
			(error) => {
				runIfMounted(() => {
					setDeleting(prev => { const n = { ...prev }; delete n[slot.key]; return n; });
					setUploadError(prev => ({ ...prev, [slot.key]: (error && error.message) || 'Delete failed' }));
				});
			}
		);
	}
	return (<div className="mtop--15">
		<label className="text--bold">Font family</label>
		<div className="text--muted font--12">Choose a standard font, a Google Font, or upload your own.</div>

		<div className="flex flex--row mtop--10">
			<button className={fontSource === 'system' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setFontSource('system')}>Standard</button>
			<button className={fontSource === 'google' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setFontSource('google')}>Google Fonts</button>
			<button className={fontSource === 'custom' ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setFontSource('custom')}>Upload</button>
		</div>

		{fontSource === 'system' &&
			<div
				className="mtop--10"
				style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}
			>
				{systemFonts.map((f) => (
					<div
						className={getFontItemCss(f, 'system')}
						key={f}
						style={{ fontFamily: f, textAlign: 'center' }}
						onClick={() => selectSystemFont(f)}
					>
						{f}
					</div>
				))}
			</div>
		}

		{fontSource === 'google' &&
			<>
				<div
					className="mtop--10"
					style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}
				>
					{googleFonts.map((f) => (
						<div
							className={getFontItemCss(f, 'google')}
							key={f}
							style={{ fontFamily: f, textAlign: 'center' }}
							onClick={() => selectCuratedFont(f)}
						>
							{f}
						</div>
					))}
					<div
						className={`dropdown__item${showOther ? ' dropdown__item-active' : ''}`}
						style={{ textAlign: 'center' }}
						onClick={openOther}
					>
						Other…
					</div>
				</div>
				{showOther &&
					<div className="mtop--15">
						<label className="text--bold">Custom Google Font</label>
						<div className="text--muted font--12">Type the exact name as shown on Google Fonts — it’s case- and spacing-sensitive.</div>
						<input
							type="text"
							className="input btn-100 mtop--10"
							placeholder="e.g. Playfair Display"
							value={customFont}
							onChange={(e) => { setCustomFont(e.target.value); setCustomFontStatus('idle'); }}
							onBlur={() => verifyCustomFont(customFont)}
							onKeyDown={(e) => { if(e.key === 'Enter') verifyCustomFont(customFont); }}
							style={{ fontFamily: customFontStatus === 'ok' ? customFont : 'inherit' }}
						/>
						{customFontStatus === 'checking' &&
							<div className="text--muted font--12 mtop--10">Checking…</div>
						}
						{customFontStatus === 'ok' &&
							<div className="font--12 mtop--10" style={{ color: '#2e7d32' }}>
								✓ “{customFont}” loaded and applied.
							</div>
						}
						{customFontStatus === 'error' &&
							<div className="font--12 mtop--10" style={{ color: '#c0392b' }}>
								Couldn’t find “{customFont}” on Google Fonts. Check the exact spelling and capitalization.
							</div>
						}
					</div>
				}
			</>
		}

		{fontSource === 'custom' &&
			<div className="mtop--10">
				{(font?.custom?.files?.length > 0 && font?.source !== 'custom') &&
					<div className="card card__warning mtop--10 text--center">
						<div className="font--12">This uploaded font is saved but not currently applied.</div>
						<button className="btn btn-primary mtop--10" onClick={applyCustomFont}>Apply this font</button>
					</div>
				}

				<label className="text--bold">Font name</label>
				<div className="text--muted font--12">A label to help you recognize this font.</div>
				<input
					type="text"
					className="input btn-100 mtop--10"
					placeholder="e.g. Acme Sans"
					value={customName}
					onChange={(e) => setCustomName(e.target.value)}
					onBlur={renameCustom}
				/>

				<label className="mtop--15 font--12" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
					<input type="checkbox" checked={licenseConfirmed} onChange={(e) => setLicenseConfirmed(e.target.checked)} />
					I have the right to use these font files on this campaign.
				</label>

				<div className="text--muted font--12 mtop--10">Accepted formats: .woff2, .woff, .ttf, .otf. Regular is required.</div>

				<div className="mtop--10">
					{fontSlots.map((slot) => {
						const existing = getFontFile(slot.weight, slot.style);
						const pct = uploadProgress[slot.key];
						const err = uploadError[slot.key];
						return (
							<div
								key={slot.key}
								className="mtop--10"
								style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
							>
								<div style={{ flex: 1, minWidth: 0 }}>
									<label className="text--bold">{slot.label}{slot.required ? ' *' : ''}</label>
									{existing
										? <div className="font--12" style={{ fontFamily: CUSTOM_FONT_FAMILY, fontWeight: slot.weight, fontStyle: slot.style }}>The quick brown fox</div>
										: <div className="text--muted font--12">{slot.required ? 'Required' : 'Optional'}</div>}
									{err && <div className="font--12" style={{ color: '#c0392b' }}>{err}</div>}
								</div>
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									{deleting[slot.key]
										? <span className="text--muted font--12">Removing…</span>
										: typeof pct === 'number'
											? <span className="text--muted font--12">Uploading… {pct}%</span>
											: <>
													{licenseConfirmed
														? <label className="btn btn-secondary" style={{ cursor: 'pointer', margin: 0 }}>
																{existing ? 'Replace' : 'Upload'}
																<input
																	type="file"
																	accept={ACCEPTED_FONT_EXTENSIONS.map(e => '.' + e).join(',')}
																	style={{ display: 'none' }}
																	onChange={(e) => { handleFontFileSelect(slot, e.target.files); e.target.value = ''; }}
																/>
															</label>
														: <button className="btn btn-secondary" disabled style={{ opacity: 0.5 }}>Upload</button>}
													{existing && !slot.required &&
														<span
															className="material-icons font--20"
															title="Remove this style"
															style={{ cursor: 'pointer', color: '#c0392b' }}
															onClick={() => removeFontFile(slot)}
														>
															delete
														</span>
													}
												</>}
								</div>
							</div>
						);
					})}
				</div>

				{(font?.custom?.files?.length > 0) &&
					<div className="mtop--15">
						<button
							className="btn btn-secondary btn-100"
							disabled={deleting['__all__']}
							style={{ color: '#c0392b' }}
							onClick={() => {
								if(typeof window !== 'undefined' &&
									!window.confirm('Remove the uploaded font and reset to the default (Arial)? This deletes all uploaded styles.'))
									return;
								removeWholeCustomFont();
							}}
						>
							{deleting['__all__'] ? 'Removing…' : 'Remove entire font'}
						</button>
						{uploadError['__all__'] &&
							<div className="font--12 mtop--10" style={{ color: '#c0392b' }}>{uploadError['__all__']}</div>}
					</div>
				}
			</div>
		}
	</div>);
}
